import { api } from '../core/api/httpClient';
export { extractErrorMessage } from '../core/api/errorUtils';

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Stores endpoints
export const storesAPI = {
  list: () => api.get('/stores'),
  get: (id) => api.get(`/stores/${id}`),
  create: (data) => api.post('/stores', data),
  update: (id, data) => api.put(`/stores/${id}`, data),
  completeOnboarding: (id) => api.post(`/stores/${id}/complete-onboarding`),
  updateModules: (id, modules) => api.put(`/stores/${id}/modules`, modules),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/stores/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Categories endpoints
export const categoriesAPI = {
  list: (storeId) => api.get(`/categories?store_id=${storeId}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Products endpoints
export const productsAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/products?${queryParams}`);
  },
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  uploadImage: (productId, storeId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/products/${productId}/image?store_id=${storeId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Inventory endpoints
export const inventoryAPI = {
  list: (storeId, categoryId = null) => {
    let url = `/inventory?store_id=${storeId}`;
    if (categoryId) url += `&category_id=${categoryId}`;
    return api.get(url);
  },
  update: (storeId, productId, data) =>
    api.post(`/inventory/update?store_id=${storeId}&product_id=${productId}`, data),
  getLowStock: (storeId) => api.get(`/inventory/low-stock?store_id=${storeId}`),
};

// Sales endpoints
export const salesAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/sales?${queryParams}`);
  },
  create: (data) => api.post('/sales', data),
  get: (id) => api.get(`/sales/${id}`),
};

// Rentals endpoints
export const rentalsAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/rentals?${queryParams}`);
  },
  create: (data) => api.post('/rentals', data),
  get: (id) => api.get(`/rentals/${id}`),
  update: (id, data) => api.put(`/rentals/${id}`, data),
};

// Forecast endpoints
export const forecastAPI = {
  getProduct: (productId, storeId, type = '7_day') =>
    api.get(`/forecast/${productId}?store_id=${storeId}&forecast_type=${type}`),
  getSummary: (storeId) => api.get(`/forecast/summary?store_id=${storeId}`),
  generate: (storeId) => api.post(`/forecast/generate?store_id=${storeId}`),
};

// Orders endpoints
export const ordersAPI = {
  list: (storeId, status = null) => {
    let url = `/orders?store_id=${storeId}`;
    if (status) url += `&status_filter=${status}`;
    return api.get(url);
  },
  get: (id) => api.get(`/orders/${id}`),
  generate: (storeId) => api.post(`/orders/generate?store_id=${storeId}`),
  approve: (id) => api.put(`/orders/${id}/approve`),
  update: (id, data) => api.put(`/orders/${id}`, data),
  listSuppliers: (storeId) => api.get(`/orders/suppliers?store_id=${storeId}`),
  createSupplier: (data) => api.post('/orders/suppliers', data),
  updateSupplier: (id, data) => api.put(`/orders/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/orders/suppliers/${id}`),
};

// Dashboard endpoints
export const dashboardAPI = {
  getOverview: (storeId, days = 30) => api.get(`/dashboard/overview?store_id=${storeId}&days=${days}`),
};

// Customers endpoints
export const customersAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/customers?${queryParams}`);
  },
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Expenses endpoints
export const expensesAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/expenses?${queryParams}`);
  },
  get: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getSummary: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/expenses/summary?${queryParams}`);
  },
};

// Bookings endpoints
export const bookingsAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/bookings?${queryParams}`);
  },
  get: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  update: (id, data) => api.put(`/bookings/${id}`, data),
  delete: (id) => api.delete(`/bookings/${id}`),
  getTodayActive: (storeId) => api.get(`/bookings/today-active?store_id=${storeId}`),
  getDueReturns: (storeId) => api.get(`/bookings/due-returns?store_id=${storeId}`),
  getProductBookings: (productId) => api.get(`/bookings/product/${productId}`),
  getProductUpcoming: (productId) => api.get(`/bookings/product/${productId}/upcoming`),
  sendConfirmation: (id) => api.post(`/bookings/${id}/send-confirmation`),
};

// Invoices endpoints
export const invoicesAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/invoices?${queryParams}`);
  },
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  download: (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' }),
  getWhatsAppLink: (id) => api.get(`/invoices/${id}/whatsapp-link`),
};

// Payments endpoints
export const paymentsAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/payments?${queryParams}`);
  },
  get: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  getBookingSummary: (bookingId) => api.get(`/payments/booking/${bookingId}/summary`),
  getTypes: () => api.get('/payments/types/list'),
  getMethods: () => api.get('/payments/methods/list'),
};

// Returns endpoints
export const returnsAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/returns?${queryParams}`);
  },
  get: (id) => api.get(`/returns/${id}`),
  create: (data) => api.post('/returns', data),
  update: (id, data) => api.put(`/returns/${id}`, data),
  getBookingReturns: (bookingId) => api.get(`/returns/booking/${bookingId}`),
};

// Maintenance endpoints
export const maintenanceAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/maintenance?${queryParams}`);
  },
  getByProduct: (productId) => api.get(`/maintenance/product/${productId}`),
  get: (id) => api.get(`/maintenance/${id}`),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.put(`/maintenance/${id}`, data),
  delete: (id) => api.delete(`/maintenance/${id}`),
  getTypes: () => api.get('/maintenance/types/list'),
};

// Staff endpoints
export const staffAPI = {
  getRoles: () => api.get('/staff/roles'),
  listStoreStaff: (storeId) => api.get(`/staff/stores/${storeId}`),
  inviteStaff: (storeId, data) => api.post(`/staff/stores/${storeId}/invite`, data),
  updateStaff: (storeId, staffId, data) => api.put(`/staff/stores/${storeId}/members/${staffId}`, data),
  removeStaff: (storeId, staffId) => api.delete(`/staff/stores/${storeId}/members/${staffId}`),
  getMyPermissions: (storeId) => api.get(`/staff/stores/${storeId}/my-permissions`),
};

// Reports endpoints
export const reportsAPI = {
  salesSummary: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/sales/summary?${queryParams}`);
  },
  salesByProduct: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/sales/by-product?${queryParams}`);
  },
  salesByCategory: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/sales/by-category?${queryParams}`);
  },
  salesByPaymentMethod: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/sales/by-payment-method?${queryParams}`);
  },
  salesTrend: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/sales/trend?${queryParams}`);
  },
  inventorySummary: (storeId) => api.get(`/reports/stores/${storeId}/inventory/summary`),
  inventoryAging: (storeId) => api.get(`/reports/stores/${storeId}/inventory/aging`),
  inventoryTurnover: (storeId) => api.get(`/reports/stores/${storeId}/inventory/turnover`),
  inventoryValuation: (storeId) => api.get(`/reports/stores/${storeId}/inventory/valuation`),
  customerSummary: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/customers/summary?${queryParams}`);
  },
  topCustomers: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/customers/top?${queryParams}`);
  },
  staffPerformance: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/staff/performance?${queryParams}`);
  },
  taxSummary: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/tax/summary?${queryParams}`);
  },
  taxByRate: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/tax/by-rate?${queryParams}`);
  },
  exportSales: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/export/sales?${queryParams}`, { responseType: 'blob' });
  },
  exportInventory: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/export/inventory?${queryParams}`, { responseType: 'blob' });
  },
  rentalUtilization: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/reports/stores/${storeId}/rental-metrics/utilization?${queryParams}`);
  },
};

// Audit endpoints
export const auditAPI = {
  getStoreLogs: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/audit/stores/${storeId}?${queryParams}`);
  },
  getAvailableActions: (storeId) => api.get(`/audit/stores/${storeId}/actions`),
  getEntityHistory: (storeId, entityType, entityId) => api.get(`/audit/stores/${storeId}/entity/${entityType}/${entityId}`),
  getUserActivity: (userId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/audit/users/${userId}/activity?${queryParams}`);
  },
  getSessions: () => api.get(`/audit/sessions`),
  revokeSession: (sessionId) => api.delete(`/audit/sessions/${sessionId}`),
};

// Locations endpoints
export const locationsAPI = {
  list: (storeId) => api.get(`/locations/stores/${storeId}`),
  create: (storeId, data) => api.post(`/locations/stores/${storeId}`, data),
  update: (locationId, data) => api.put(`/locations/${locationId}`, data),
  delete: (locationId) => api.delete(`/locations/${locationId}`),
  getInventory: (locationId) => api.get(`/locations/${locationId}/inventory`),
  transfer: (storeId, data) => api.post(`/locations/stores/${storeId}/transfer`, data),
  adjust: (storeId, data) => api.post(`/locations/stores/${storeId}/adjust`, data),
  receive: (storeId, data) => api.post(`/locations/stores/${storeId}/receive`, data),
  getMovements: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/locations/stores/${storeId}/movements?${queryParams}`);
  },
  listBatches: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/locations/stores/${storeId}/batches?${queryParams}`);
  },
  getExpiringBatches: (storeId, days = 30) =>
    api.get(`/locations/stores/${storeId}/batches/expiring?days=${days}`),
};

// Subscriptions endpoints — USD only, no region/currency params accepted
export const subscriptionsAPI = {
  getTiers: () => api.get('/subscriptions/tiers'),
  getStoreSubscription: (storeId) => api.get(`/subscriptions/stores/${storeId}`),
  cancel: (storeId) => api.post(`/subscriptions/stores/${storeId}/cancel`),
  // Free plan: activates directly, no Stripe
  selectFreePlan: (storeId) => api.post(`/subscriptions/stores/${storeId}/select-free`),
  // Paid plans (basic/starter/growth): provider-agnostic checkout (Stripe or Paddle)
  createCheckout: (storeId, tier) => api.post(`/subscriptions/create-checkout`, { tier }),
  getBillingPortal: (storeId) => api.post(`/subscriptions/stores/${storeId}/portal`),
  getPaymentHistory: (storeId) => api.get(`/subscriptions/stores/${storeId}/payment-history`),
  // Called from /billing/success after Stripe redirect
  verifySession: (params) => api.get('/subscriptions/verify-session', { params }),
};

// Notifications endpoints
export const notificationsAPI = {
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/notifications/me?${queryParams}`);
  },
  getUnreadCount: (storeId) => api.get(`/notifications/unread-count?store_id=${storeId}`),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: (storeId, notificationIds = null) =>
    api.post(`/notifications/mark-read?store_id=${storeId}`, { notification_ids: notificationIds }),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
  getPreferences: (storeId) => api.get(`/notifications/preferences?store_id=${storeId}`),
  updatePreferences: (storeId, data) => api.put(`/notifications/preferences?store_id=${storeId}`, data),
};

// Approvals endpoints
export const approvalsAPI = {
  listPending: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/approvals/pending?${queryParams}`);
  },
  getMyRequests: (storeId, params = {}) => {
    const queryParams = new URLSearchParams({ store_id: storeId, ...params });
    return api.get(`/approvals/my-requests?${queryParams}`);
  },
  get: (requestId) => api.get(`/approvals/${requestId}`),
  create: (data) => api.post(`/approvals/request`, data),
  respond: (requestId, data) => api.post(`/approvals/${requestId}/respond`, data),
  execute: (requestId) => api.post(`/approvals/${requestId}/execute`),
  cancel: (requestId) => api.post(`/approvals/${requestId}/cancel`),
  // Approval Rules
  listRules: (storeId, activeOnly = true) => api.get(`/approvals/rules/store/${storeId}?active_only=${activeOnly}`),
  createRule: (data) => api.post(`/approvals/rules`, data),
  updateRule: (ruleId, data) => api.put(`/approvals/rules/${ruleId}`, data),
  deleteRule: (ruleId) => api.delete(`/approvals/rules/${ruleId}`),
  checkApproval: (storeId, data) => api.post(`/approvals/check?store_id=${storeId}`, data),
};

// Write-off endpoints (uses locations)
export const writeOffAPI = {
  create: (storeId, data) => api.post(`/locations/stores/${storeId}/write-off`, data),
};

// Shifts endpoints
export const shiftsAPI = {
  getCurrent: (storeId) => api.get(`/shifts/current?store_id=${storeId}`),
  open: (data) => api.post('/shifts/open', data),
  close: (shiftId, data) => api.put(`/shifts/${shiftId}/close`, data),
  get: (shiftId) => api.get(`/shifts/${shiftId}`),
  list: (storeId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/shifts/store/${storeId}?${queryParams}`);
  },
  startBreak: (shiftId) => api.post(`/shifts/${shiftId}/break/start`),
  endBreak: (shiftId) => api.post(`/shifts/${shiftId}/break/end`),
  listBreaks: (shiftId) => api.get(`/shifts/${shiftId}/breaks`),
  addCashMovement: (shiftId, data) => api.post(`/shifts/${shiftId}/cash-movement`, data),
  listCashMovements: (shiftId) => api.get(`/shifts/${shiftId}/cash-movements`),
  dailyClosure: (storeId, date) => api.post('/shifts/daily-closure', { store_id: storeId, date }),
};

// Admin endpoints
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  listStores: (params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/admin/stores?${queryParams}`);
  },
  getStore: (storeId) => api.get(`/admin/stores/${storeId}`),
  overrideSubscription: (storeId, data) => api.put(`/admin/stores/${storeId}/subscription`, data),
  suspendStore: (storeId) => api.post(`/admin/stores/${storeId}/suspend`),
  reactivateStore: (storeId) => api.post(`/admin/stores/${storeId}/reactivate`),
  listUsers: (params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/admin/users?${queryParams}`);
  },
  getSubscriptionSummary: () => api.get('/admin/subscriptions/summary'),
};

// ==================== DATA IMPORT ====================
export const importsAPI = {
  upload: (storeId, formData) => api.post(`/imports/stores/${storeId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  setMapping: (storeId, importId, fieldMapping) =>
    api.put(`/imports/stores/${storeId}/${importId}/mapping`, { field_mapping: fieldMapping }),
  execute: (storeId, importId) =>
    api.post(`/imports/stores/${storeId}/${importId}/execute`),
  rollback: (storeId, importId) =>
    api.post(`/imports/stores/${storeId}/${importId}/rollback`),
  list: (storeId, params = {}) => {
    const q = new URLSearchParams(params);
    return api.get(`/imports/stores/${storeId}?${q}`);
  },
  get: (storeId, importId) =>
    api.get(`/imports/stores/${storeId}/${importId}`),
  getFieldDefinitions: (importType) =>
    api.get(`/imports/field-definitions/${importType}`),
};

// ==================== ANALYTICS & PREDICTIONS ====================
export const analyticsAPI = {
  getOverview: (storeId) => api.get(`/analytics/stores/${storeId}/overview`),
  getForecast: (storeId, params = {}) => {
    const q = new URLSearchParams(params);
    return api.get(`/analytics/stores/${storeId}/forecast?${q}`);
  },
  getDeadStock: (storeId, params = {}) => {
    const q = new URLSearchParams(params);
    return api.get(`/analytics/stores/${storeId}/dead-stock?${q}`);
  },
  getRecommendations: (storeId, params = {}) => {
    const q = new URLSearchParams(params);
    return api.get(`/analytics/stores/${storeId}/recommendations?${q}`);
  },
  recalculate: (storeId) => api.post(`/analytics/stores/${storeId}/recalculate`),
};

// ==================== STAFF INVITATIONS ====================
export const invitationsAPI = {
  create: (storeId, data) => api.post(`/invitations/stores/${storeId}`, data),
  list: (storeId, includeExpired = false) =>
    api.get(`/invitations/stores/${storeId}?include_expired=${includeExpired}`),
  resend: (storeId, invitationId) =>
    api.post(`/invitations/stores/${storeId}/${invitationId}/resend`),
  revoke: (storeId, invitationId) =>
    api.delete(`/invitations/stores/${storeId}/${invitationId}`),
  accept: (token) => api.post('/invitations/accept', { token }),
  resetPassword: (currentPassword, newPassword) =>
    api.post('/invitations/reset-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// ==================== OFFER CAMPAIGNS ====================
export const campaignsAPI = {
  create: (storeId, data) => api.post(`/campaigns/stores/${storeId}`, data),
  list: (storeId, params = {}) => {
    const q = new URLSearchParams(params);
    return api.get(`/campaigns/stores/${storeId}?${q}`);
  },
  get: (storeId, campaignId) =>
    api.get(`/campaigns/stores/${storeId}/${campaignId}`),
  activate: (storeId, campaignId) =>
    api.post(`/campaigns/stores/${storeId}/${campaignId}/activate`),
  sendWhatsApp: (storeId, campaignId) =>
    api.post(`/campaigns/stores/${storeId}/${campaignId}/send-whatsapp`),
  expire: (storeId, campaignId) =>
    api.post(`/campaigns/stores/${storeId}/${campaignId}/expire`),
  getPerformance: (storeId, campaignId) =>
    api.get(`/campaigns/stores/${storeId}/${campaignId}/performance`),
};

export const deadStockCampaignAPI = {
  getDeadStock: (storeId) => api.get(`/campaigns/dead-stock?store_id=${storeId}`),
  getTargetCustomers: (storeId, segment = 'recent_buyers') =>
    api.get(`/campaigns/target-customers?store_id=${storeId}&segment=${segment}`),
  generateMessage: (data) => api.post('/campaigns/generate-message', data),
  sendCampaign: (data) => api.post('/campaigns/send', data),
  getHistory: (storeId, limit = 20) => api.get(`/campaigns/dead-stock/history?store_id=${storeId}&limit=${limit}`),
};

export default api;

// Onboarding
export const onboardingAPI = {
  getStatus: (storeId) => api.get(`/onboarding/status?store_id=${storeId}`),
  completeStep: (storeId, stepKey) =>
    api.post(`/onboarding/complete/${stepKey}?store_id=${storeId}`),
  seedDemo: (storeId) => api.post(`/onboarding/seed-demo?store_id=${storeId}`),
};

// Variants
export const variantsAPI = {
  list: (storeId, productId) => api.get(`/variants?store_id=${storeId}&product_id=${productId}`),
  create: (data) => api.post('/variants', data),
  update: (id, data) => api.put(`/variants/${id}`, data),
  delete: (id, storeId) => api.delete(`/variants/${id}?store_id=${storeId}`),
  listAttributes: (storeId) => api.get(`/variants/attributes?store_id=${storeId}`),
  createAttribute: (data) => api.post('/variants/attributes', data),
};

// API Keys
export const apiKeysAPI = {
  list: (storeId) => api.get(`/api-keys?store_id=${storeId}`),
  create: (data) => api.post('/api-keys', data),
  delete: (id, storeId) => api.delete(`/api-keys/${id}?store_id=${storeId}`),
};

// Z Reports
export const zReportAPI = {
  generate: (shiftId) => api.post(`/shifts/${shiftId}/z-report`),
  get: (shiftId) => api.get(`/shifts/${shiftId}/z-report`),
};

// Split payments
export const splitPaymentsAPI = {
  create: (saleId, data) => api.post(`/sales/${saleId}/splits`, data),
  get: (saleId) => api.get(`/sales/${saleId}/payment-splits`),
};

// Accounting exports (CSV — QuickBooks / Tally / Zoho compatible)
export const accountingExportsAPI = {
  downloadSales: (storeId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/exports/stores/${storeId}/sales${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
  },
  downloadInventory: (storeId) =>
    api.get(`/exports/stores/${storeId}/inventory`, { responseType: 'blob' }),
  downloadExpenses: (storeId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/exports/stores/${storeId}/expenses${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
  },
};

// Web Push subscriptions
export const pushAPI = {
  subscribe: (data) => api.post('/push/subscribe', data),
  unsubscribe: (endpoint) => api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`),
  sendTest: (data) => api.post('/push/test', data),
};

// Password reset
export const passwordResetAPI = {
  forgot: (email) => api.post('/auth/forgot-password', { email }),
  reset: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Commission system (admin + salesperson self-service)
export const commissionsAPI = {
  // Admin
  createSalesperson: (data) => api.post('/commissions/salespeople', data),
  listSalespeople: () => api.get('/commissions/salespeople'),
  getStats: (spId) => api.get(`/commissions/salespeople/${spId}/stats`),
  listCommissions: (spId, status) =>
    api.get(`/commissions/salespeople/${spId}/commissions${status ? `?status_filter=${status}` : ''}`),
  approveCommission: (id) => api.patch(`/commissions/commissions/${id}/approve`),
  createPayout: (data) => api.post('/commissions/payouts', data),
  // Salesperson self-service
  myStats: () => api.get('/commissions/my/stats'),
  myCommissions: () => api.get('/commissions/my/commissions'),
};
